/**
 * Songs Layer Strategy Implementation
 *
 * Implements layer-specific logic for Songs (G) layer processing.
 * Songs layer handles audio files only.
 */

import { validateSongsMetadata } from '../../../utils/metadataValidation';
import {
  SONG_METADATA,
  validateStringMaxLength,
  validateArrayLength,
  validateArrayItems,
} from '../../../constants/apiMetadataConstraints';
import {
    BaseLayerStrategy,
    LayerFieldConfig,
    ValidationResult,
} from './LayerStrategy';

export interface SongsLayerMetadata {
  layerType: 'songs';

  // Core song information - Use correct field names that match actual data structure
  songName?: string;
  artistName?: string;
  albumName?: string;
  songTitle?: string; // Backward compatibility
  artist?: string; // Backward compatibility
  album?: string; // Backward compatibility
  releaseYear?: number;

  // Audio characteristics
  duration?: number; // seconds
  tempo?: number; // BPM
  key?: string;
  genre?: string[];
  mood?: string[];

  // Technical details
  bitrate?: number;
  sampleRate?: number;
  channels?: number;
  format?: string;

  // Music metadata
  lyrics?: string;
  composer?: string;
  producer?: string;
  label?: string;
  isrc?: string; // International Standard Recording Code

  // AI-generated fields
  energy?: 'low' | 'medium' | 'high';
  acousticness?: number; // 0-1
  instrumentalness?: number; // 0-1
  valence?: number; // 0-1 (positivity)
}

export class SongsLayerStrategy extends BaseLayerStrategy {
  readonly layerCode = 'G';
  readonly layerName = 'Songs';
  readonly layerDescription = 'Audio tracks and musical compositions';

  getRequiredFields(): string[] {
    // Updated to match actual backend API requirements (SongMetadataRequest)
    // Only fields marked as required (not optional) in the API contract
    return [
      'songName',
      'artistName',
      'genre',
      'mood',
      'tempo',
    ];
  }

  getOptionalFields(): string[] {
    return [
      'album',
      'releaseYear',
      'duration',
      'tempo',
      'key',
      'genre',
      'mood',
      'bitrate',
      'sampleRate',
      'channels',
      'format',
      'lyrics',
      'composer',
      'producer',
      'label',
      'isrc',
      'energy',
      'acousticness',
      'instrumentalness',
      'valence',
    ];
  }

  getFieldConfig(): LayerFieldConfig {
    return {
      required: this.getRequiredFields(),
      optional: this.getOptionalFields(),
      computed: ['duration', 'bitrate', 'sampleRate', 'format'],
      inherited: [],
    };
  }

  extractMetadataFromAI(aiResponse: any): SongsLayerMetadata {
    const layerMetadata =
      aiResponse.layerMetadata || aiResponse.songsMetadata || {};

    // Map to correct field names with backward compatibility
    const songName = layerMetadata.songName || layerMetadata.songTitle || aiResponse.songName || aiResponse.songTitle || '';
    const artistName = layerMetadata.artistName || layerMetadata.artist || aiResponse.artistName || aiResponse.artist || '';
    const albumName = layerMetadata.albumName || layerMetadata.album || aiResponse.albumName || aiResponse.album || '';

    return {
      layerType: 'songs',

      // Core information - Use correct field names
      songName,
      artistName,
      albumName,
      releaseYear: layerMetadata.releaseYear || aiResponse.releaseYear,

      // Audio characteristics
      duration: layerMetadata.duration || aiResponse.duration,
      tempo: layerMetadata.tempo || aiResponse.tempo,
      key: layerMetadata.key || aiResponse.key,
      genre: this.normalizeArray(layerMetadata.genre || aiResponse.genre),
      mood: this.normalizeArray(layerMetadata.mood || aiResponse.mood),

      // Technical details from audio analysis
      bitrate: layerMetadata.bitrate || aiResponse.bitrate,
      sampleRate: layerMetadata.sampleRate || aiResponse.sampleRate,
      channels: layerMetadata.channels || aiResponse.channels,
      format: layerMetadata.format || aiResponse.format,

      // Music metadata
      lyrics: layerMetadata.lyrics || aiResponse.lyrics,
      composer: layerMetadata.composer || aiResponse.composer,
      producer: layerMetadata.producer || aiResponse.producer,
      label: layerMetadata.label || aiResponse.label,
      isrc: layerMetadata.isrc || aiResponse.isrc,

      // AI-generated characteristics
      energy: this.normalizeEnergy(layerMetadata.energy || aiResponse.energy),
      performanceContext: this.normalizeArray(
        layerMetadata.performanceContext || aiResponse.performanceContext
      ),
      targetAudience: this.normalizeArray(
        layerMetadata.targetAudience || aiResponse.targetAudience
      ),
      culturalContext: this.normalizeArray(
        layerMetadata.culturalContext || aiResponse.culturalContext
      ),
      musicalStyle: this.normalizeArray(
        layerMetadata.musicalStyle || aiResponse.musicalStyle
      ),
      ageAppropriateness: this.normalizeArray(
        layerMetadata.ageAppropriateness || aiResponse.ageAppropriateness
      ),
      acousticness: layerMetadata.acousticness || aiResponse.acousticness,
      instrumentalness:
        layerMetadata.instrumentalness || aiResponse.instrumentalness,
      valence: layerMetadata.valence || aiResponse.valence,
    } as any; // Type assertion needed due to interface mismatch with local interface
  }

  enhanceWithDefaults(metadata: SongsLayerMetadata): SongsLayerMetadata {
    const metadataAny = metadata as any;
    return {
      ...this.getDefaultMetadata(),
      ...metadata,
      layerType: 'songs',

      // Ensure required fields have defaults - Use correct field names with backward compatibility
      songName: metadataAny.songName || metadata.songTitle || 'Untitled',
      artistName: metadataAny.artistName || metadata.artist || 'Unknown Artist',
      albumName: metadataAny.albumName || metadata.album || '',

      // Normalize arrays
      genre: this.normalizeArray(metadata.genre),
      mood: this.normalizeArray(metadata.mood),

      // Set defaults for audio characteristics
      energy: metadata.energy || 'medium',
      tempo: metadata.tempo || 120, // Default BPM
    };
  }

  generateTags(metadata: SongsLayerMetadata, description?: string): string[] {
    const tags = new Set<string>();

    // Basic layer tags
    tags.add('songs');
    tags.add('audio');
    tags.add('music');

    // Genre tags
    if (metadata.genre && metadata.genre.length > 0) {
      metadata.genre.forEach(g => {
        tags.add(g.toLowerCase());
        tags.add(`${g.toLowerCase()}-music`);
      });
    }

    // Mood tags
    if (metadata.mood && metadata.mood.length > 0) {
      metadata.mood.forEach(m => {
        tags.add(`${m.toLowerCase()}-mood`);
        tags.add(m.toLowerCase());
      });
    }

    // Energy tags
    if (metadata.energy) {
      tags.add(`${metadata.energy}-energy`);
      tags.add(`${metadata.energy}-tempo`);
    }

    // Tempo-based tags
    if (metadata.tempo) {
      if (metadata.tempo < 80) {
        tags.add('slow-tempo');
        tags.add('ballad');
      } else if (metadata.tempo < 120) {
        tags.add('medium-tempo');
        tags.add('moderate');
      } else {
        tags.add('fast-tempo');
        tags.add('upbeat');
      }
    }

    // Year-based tags
    if (metadata.releaseYear) {
      const decade = Math.floor(metadata.releaseYear / 10) * 10;
      tags.add(`${decade}s`);
      tags.add(`${decade}s-music`);
    }

    // Feature tags
    if (metadata.acousticness && metadata.acousticness > 0.7) {
      tags.add('acoustic');
      tags.add('unplugged');
    }

    if (metadata.instrumentalness && metadata.instrumentalness > 0.7) {
      tags.add('instrumental');
      tags.add('no-vocals');
    }

    // Artist tag - Check both field names for backward compatibility
    const artistName = (metadata as any).artistName || (metadata as any).artist;
    if (artistName) {
      tags.add(artistName.toLowerCase().replace(/\s+/g, '-'));
    }

    return Array.from(tags).slice(0, 30);
  }

  categorizeTags(tags: string[]): {
    primary: string[];
    secondary: string[];
    descriptive: string[];
  } {
    const categories = {
      primary: [] as string[],
      secondary: [] as string[],
      descriptive: [] as string[],
    };

    tags.forEach(tag => {
      const lowerTag = tag.toLowerCase();

      // Primary tags - genre and type
      if (
        lowerTag.includes('music') ||
        lowerTag.includes('genre') ||
        ['pop', 'rock', 'hip-hop', 'jazz', 'classical', 'electronic'].includes(
          lowerTag
        )
      ) {
        categories.primary.push(tag);
      }
      // Secondary tags - mood and energy
      else if (
        lowerTag.includes('mood') ||
        lowerTag.includes('energy') ||
        lowerTag.includes('tempo')
      ) {
        categories.secondary.push(tag);
      }
      // Descriptive tags - general attributes
      else {
        categories.descriptive.push(tag);
      }
    });

    return categories;
  }

  // Override validateMetadata to integrate AlgoRhythm-specific validation
  validateMetadata(metadata: any): ValidationResult {
    // First, call base class validation (checks required fields from getRequiredFields())
    const baseValidation = super.validateMetadata(metadata);
    
    // Then, apply AlgoRhythm-specific detailed validation
    // Cast to any first to handle type differences between local and imported SongsLayerMetadata
    const algorhythmValidation = validateSongsMetadata(metadata as any);
    
    // Merge results: combine errors and warnings from both validations
    // Map AlgoRhythm validation errors to match LayerStrategy ValidationError format
    const combinedErrors = [
      ...baseValidation.errors,
      ...algorhythmValidation.errors.map(e => ({
        field: e.field,
        type: 'required' as const,
        message: e.message,
        severity: 'error' as const,
      })),
    ];
    
    const combinedWarnings = [
      ...baseValidation.warnings,
      ...algorhythmValidation.warnings.map(w => ({
        field: w.field,
        message: w.message,
        suggestion: undefined,
      })),
    ];
    
    // Remove duplicate errors (same field and message)
    const uniqueErrors = combinedErrors.filter((error, index, self) =>
      index === self.findIndex(e => e.field === error.field && e.message === error.message)
    );
    
    const uniqueWarnings = combinedWarnings.filter((warning, index, self) =>
      index === self.findIndex(w => w.field === warning.field && w.message === warning.message)
    );
    
    return {
      isValid: uniqueErrors.filter(e => e.severity === 'error').length === 0,
      errors: uniqueErrors,
      warnings: uniqueWarnings,
    };
  }

  validateBusinessRules(metadata: SongsLayerMetadata): ValidationResult {
    const errors: any[] = [];
    const warnings: any[] = [];

    // OpenAPI: string maxLength 255
    const songName = (metadata as any).songName || (metadata as any).songTitle;
    if (songName) {
      const msg = validateStringMaxLength(songName, SONG_METADATA.stringMaxLength, 'Song name');
      if (msg) errors.push({ field: 'songName', message: msg, severity: 'error' });
      else if (songName.trim().length < 2) {
        warnings.push({
          field: 'songName',
          message: 'Song name should be at least 2 characters long',
          suggestion: 'Provide a more descriptive song name',
        });
      }
    }
    if ((metadata as any).artistName) {
      const msg = validateStringMaxLength((metadata as any).artistName, SONG_METADATA.stringMaxLength, 'Artist name');
      if (msg) errors.push({ field: 'artistName', message: msg, severity: 'error' });
    }
    // OpenAPI: genre/mood arrays min 1 max 50
    const genre = Array.isArray((metadata as any).genre) ? (metadata as any).genre : [];
    if (genre.length > 0) {
      const msg = validateArrayLength(genre, SONG_METADATA.genre, 'Genre');
      if (msg) errors.push({ field: 'genre', message: msg, severity: 'error' });
      else {
        const itemMsg = validateArrayItems(genre, SONG_METADATA.stringMaxLength, 'Genre');
        if (itemMsg) errors.push({ field: 'genre', message: itemMsg, severity: 'error' });
      }
    }
    const mood = Array.isArray((metadata as any).mood) ? (metadata as any).mood : [];
    if (mood.length > 0) {
      const msg = validateArrayLength(mood, SONG_METADATA.mood, 'Mood');
      if (msg) errors.push({ field: 'mood', message: msg, severity: 'error' });
      else {
        const itemMsg = validateArrayItems(mood, SONG_METADATA.stringMaxLength, 'Mood');
        if (itemMsg) errors.push({ field: 'mood', message: itemMsg, severity: 'error' });
      }
    }

    // Business rule: Tempo should be realistic
    if (metadata.tempo && (metadata.tempo < 20 || metadata.tempo > 300)) {
      warnings.push({
        field: 'tempo',
        message: 'Tempo appears unusual (normal range: 40-200 BPM)',
        suggestion: 'Verify the tempo value',
      });
    }

    // Business rule: Duration should be reasonable
    if (
      metadata.duration &&
      (metadata.duration < 10 || metadata.duration > 1200)
    ) {
      warnings.push({
        field: 'duration',
        message: 'Duration appears unusual (normal range: 30s-10min)',
        suggestion: 'Verify the duration',
      });
    }

    // Business rule: Year should be valid
    const currentYear = new Date().getFullYear();
    if (
      metadata.releaseYear &&
      (metadata.releaseYear < 1900 || metadata.releaseYear > currentYear)
    ) {
      errors.push({
        field: 'releaseYear',
        message: `Release year should be between 1900 and ${currentYear}`,
        severity: 'error',
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  getDefaultMetadata(): SongsLayerMetadata {
    return {
      layerType: 'songs',
      songName: '', // Use correct field name
      artistName: '', // Use correct field name
      albumName: '', // Use correct field name
      genre: [],
      mood: [],
      energy: 'medium',
      tempo: 120,
    } as any; // Type assertion needed due to interface mismatch
  }

  getInheritanceRules(): {
    [field: string]: 'always' | 'if_empty' | 'never' | 'merge';
  } {
    return {
      // Songs don't typically inherit from other assets - Use correct field names
      artistName: 'never',
      albumName: 'never',
      songName: 'never',
      // Backward compatibility
      artist: 'never',
      album: 'never',
      songTitle: 'never',

      // Could inherit genre/mood from collections
      genre: 'merge',
      mood: 'merge',
    };
  }

  private normalizeArray(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
    return [];
  }

  private normalizeEnergy(value: any): 'low' | 'medium' | 'high' {
    if (!value) return 'medium';
    const normalized = String(value).toLowerCase();

    if (normalized.includes('low')) return 'low';
    if (normalized.includes('high')) return 'high';

    return 'medium';
  }
}

// Export singleton instance
export const songsLayerStrategy = new SongsLayerStrategy();
